/**
 * Cookie consent store - GDPR/RODO + Google Consent Mode v2.
 *
 * Stan trzymany w localStorage pod kluczem 'klaso_consent_v1':
 *   { essential: true, analytics: bool, ads: bool, ts: ISO, version: 1 }
 *
 * Inne komponenty:
 *  - useConsent()  -> hook reagujacy na zmiany
 *  - getConsent()  -> sync snapshot
 *  - setConsent(c) -> zapisuje + dispatch event
 *  - hasConsent()  -> czy uzytkownik juz wybral cokolwiek
 *
 * Po setConsent emitujemy:
 *  - window event 'klaso:consent-change' z detail = nowy stan
 *  - gtag('consent', 'update', ...) gdy gtag dostepny
 */
import { useEffect, useState } from 'react';

const KEY = 'klaso_consent_v1';
const EVENT = 'klaso:consent-change';

export const DEFAULT_CONSENT = {
    essential: true,
    analytics: false,
    ads: false,
    ts: null,
    version: 1,
};

function readRaw() {
    try {
        const s = localStorage.getItem(KEY);
        if (!s) return null;
        const parsed = JSON.parse(s);
        if (!parsed || parsed.version !== 1) return null;
        return parsed;
    } catch {
        return null;
    }
}

export function getConsent() {
    return readRaw() || { ...DEFAULT_CONSENT };
}

export function hasConsent() {
    return readRaw() !== null;
}

export function setConsent(partial) {
    const next = {
        ...getConsent(),
        ...partial,
        essential: true,
        version: 1,
        ts: new Date().toISOString(),
    };
    try {
        localStorage.setItem(KEY, JSON.stringify(next));
    } catch (_) { /* private mode etc */ }

    // Google Consent Mode v2 - update
    try {
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('consent', 'update', {
                ad_storage: next.ads ? 'granted' : 'denied',
                ad_user_data: next.ads ? 'granted' : 'denied',
                ad_personalization: next.ads ? 'granted' : 'denied',
                analytics_storage: next.analytics ? 'granted' : 'denied',
            });
        }
    } catch (_) { /* ignore */ }

    try {
        window.dispatchEvent(new CustomEvent(EVENT, { detail: next }));
    } catch (_) { /* ignore */ }

    return next;
}

export function acceptAll() {
    return setConsent({ analytics: true, ads: true });
}

export function rejectAll() {
    return setConsent({ analytics: false, ads: false });
}

/**
 * Defaulty Consent Mode v2 - wywolac JAK NAJWCZESNIEJ przed jakimkolwiek gtag.
 * Bezpiecznie wolac wiele razy.
 */
export function installConsentDefaults() {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
        window.gtag = function () { window.dataLayer.push(arguments); };
    }
    // Domyslne ustawienia: wszystko zabronione poza essential
    window.gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
        wait_for_update: 500,
    });
    // Jesli uzytkownik juz wybral wczesniej - od razu push update
    const c = readRaw();
    if (c) {
        window.gtag('consent', 'update', {
            ad_storage: c.ads ? 'granted' : 'denied',
            ad_user_data: c.ads ? 'granted' : 'denied',
            ad_personalization: c.ads ? 'granted' : 'denied',
            analytics_storage: c.analytics ? 'granted' : 'denied',
        });
    }
}

export function useConsent() {
    const [state, setState] = useState(() => getConsent());

    useEffect(() => {
        const onChange = (e) => setState(e.detail || getConsent());
        window.addEventListener(EVENT, onChange);
        return () => window.removeEventListener(EVENT, onChange);
    }, []);

    return state;
}

export function useHasConsent() {
    const [decided, setDecided] = useState(() => hasConsent());
    useEffect(() => {
        const onChange = () => setDecided(hasConsent());
        window.addEventListener(EVENT, onChange);
        return () => window.removeEventListener(EVENT, onChange);
    }, []);
    return decided;
}

export const CONSENT_EVENT = EVENT;
